import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import RobotVideo from './RobotVideo';
import './ControlPanel.css';

const DEFAULT_VELOCITY = '0.1';
const MAX_VELOCITY = 1.0;
const VELOCITY_STEP = '0.05';

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const WheatIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22V8" />
    <path d="M12 8c-2.5 0-4-1.8-4-4 2.5 0 4 1.8 4 4z" fill="currentColor" stroke="none" />
    <path d="M12 8c2.5 0 4-1.8 4-4-2.5 0-4 1.8-4 4z" fill="currentColor" stroke="none" />
    <path d="M12 13c-2.5 0-4-1.8-4-4 2.5 0 4 1.8 4 4z" fill="currentColor" stroke="none" />
    <path d="M12 13c2.5 0 4-1.8 4-4-2.5 0-4 1.8-4 4z" fill="currentColor" stroke="none" />
    <path d="M12 18c-2.5 0-4-1.8-4-4 2.5 0 4 1.8 4 4z" fill="currentColor" stroke="none" />
    <path d="M12 18c2.5 0 4-1.8 4-4-2.5 0-4 1.8-4 4z" fill="currentColor" stroke="none" />
  </svg>
);

const TractorIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 13V8h5l2 5" />
    <path d="M9 8V5h4v8" />
    <path d="M13 10h5l1.5 4.5" />
    <circle cx="7" cy="17" r="3.2" />
    <circle cx="7" cy="17" r="0.8" fill="currentColor" stroke="none" />
    <circle cx="17.5" cy="17.5" r="2.4" />
    <circle cx="17.5" cy="17.5" r="0.7" fill="currentColor" stroke="none" />
  </svg>
);

const BarnIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 11l9-7 9 7" />
    <path d="M5 10v10h14V10" />
    <path d="M9 20v-6h6v6" />
    <path d="M9 14l6 6M15 14l-6 6" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
  </svg>
);

const XCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const ChartIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="6" y1="20" x2="6" y2="12" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="18" y1="20" x2="18" y2="15" />
  </svg>
);

const SlidersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
    <circle cx="9" cy="6" r="2" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" />
    <circle cx="7" cy="18" r="2" fill="currentColor" stroke="none" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-2.6-6.4" />
    <path d="M21 3v6h-6" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.9 17.9A9.6 9.6 0 0 1 12 19c-6.5 0-10-7-10-7a17.6 17.6 0 0 1 4.1-4.9" />
    <path d="M9.9 5.2A9.5 9.5 0 0 1 12 5c6.5 0 10 7 10 7a17.7 17.7 0 0 1-2.2 3.1" />
    <path d="m2 2 20 20" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </svg>
);

const BatteryIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="7" width="17" height="10" rx="2" />
    <line x1="22" y1="10" x2="22" y2="14" />
    <line x1="6" y1="10.5" x2="6" y2="13.5" />
    <line x1="10" y1="10.5" x2="10" y2="13.5" />
  </svg>
);

const SignalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="18" x2="5" y2="15" />
    <line x1="10" y1="18" x2="10" y2="11" />
    <line x1="15" y1="18" x2="15" y2="7" />
    <line x1="20" y1="18" x2="20" y2="4" />
  </svg>
);

const BroadcastIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <path d="M8.5 15.5a5 5 0 0 1 0-7M15.5 8.5a5 5 0 0 1 0 7" />
    <path d="M5.6 18.4a9 9 0 0 1 0-12.8M18.4 5.6a9 9 0 0 1 0 12.8" />
  </svg>
);

const StopSquareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="6" y="6" width="12" height="12" rx="2.5" fill="currentColor" />
  </svg>
);

const RobotIcon = () => (
  <svg width="72" height="72" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="14" y="20" width="36" height="28" rx="4" />
    <circle cx="24" cy="32" r="2.5" fill="currentColor" stroke="none" />
    <circle cx="40" cy="32" r="2.5" fill="currentColor" stroke="none" />
    <line x1="22" y1="40" x2="42" y2="40" />
    <line x1="32" y1="12" x2="32" y2="20" />
    <circle cx="32" cy="10" r="2" />
    <line x1="14" y1="28" x2="10" y2="28" />
    <line x1="14" y1="36" x2="10" y2="36" />
    <line x1="50" y1="28" x2="54" y2="28" />
    <line x1="50" y1="36" x2="54" y2="36" />
    <line x1="20" y1="48" x2="20" y2="54" />
    <line x1="44" y1="48" x2="44" y2="54" />
    <line x1="14" y1="54" x2="50" y2="54" />
  </svg>
);

class ReconnectingWebSocket {
  constructor(url, protocols = []) {
    this.url = url;
    this.protocols = protocols;
    this.ws = null;
    this.reconnectInterval = 1000;
    this.reconnectTimeout = null;
    this.forcedClose = false;
    this.reconnectAttempts = 0;

    this.onopen = null;
    this.onclose = null;
    this.onmessage = null;
    this.onerror = null;
    this.onstatechange = null;
    this._state = 'disconnected';

    this.connect();
  }

  _setState(state) {
    this._state = state;
    if (this.onstatechange) {
      this.onstatechange(state);
    }
  }

  connect() {
    try {
      this.ws = new WebSocket(this.url, this.protocols);

      this.ws.onopen = (event) => {
        this.reconnectAttempts = 0;
        this._setState('connected');
        if (this.onopen) this.onopen(event);
      };

      this.ws.onmessage = (event) => {
        if (this.onmessage) this.onmessage(event);
      };

      this.ws.onclose = (event) => {
        this._setState('disconnected');
        if (!this.forcedClose) {
          this.reconnectTimeout = setTimeout(() => {
            this.reconnectAttempts++;
            this.connect();
          }, this.reconnectInterval);
        }
        if (this.onclose) this.onclose(event);
      };

      this.ws.onerror = (error) => {
        if (this.onerror) this.onerror(error);
      };
    } catch (error) {
      this._setState('disconnected');
      if (!this.forcedClose) {
        this.reconnectTimeout = setTimeout(() => {
          this.reconnectAttempts++;
          this.connect();
        }, this.reconnectInterval);
      }
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    }
  }

  close() {
    this.forcedClose = true;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.ws) {
      this.ws.close();
    }
  }
}

const useClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
};

const getLogSeverity = (message) => {
  const text = String(message || '').toLowerCase();
  if (text.includes('error') || text.includes('ошибка') || text.includes('критич')) {
    return 'error';
  }
  if (text.includes('warn') || text.includes('предупрежд') || text.includes('не может')) {
    return 'warning';
  }
  return 'ok';
};

const formatLogTime = (entry) => {
  const date = new Date(entry.lastReceivedAt || entry.receivedAt);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const ControlPanel = ({ token, onLogout, language, onLanguageChange, theme, onThemeToggle, messages }) => {
  const [robots, setRobots] = useState([]);
  const [selectedRobot, setSelectedRobot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commandLoading, setCommandLoading] = useState({});
  const [velocityLoading, setVelocityLoading] = useState({});
  const [velocityInputs, setVelocityInputs] = useState({});
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const [lidarDistance, setLidarDistance] = useState(5);
  const [lidarMap, setLidarMap] = useState(null);
  const [lidarLoading, setLidarLoading] = useState(false);
  const [lidarMessage, setLidarMessage] = useState('');
  const [lidarVisible, setLidarVisible] = useState(true);
  const [activeSubmenu, setActiveSubmenu] = useState('drive');
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const selectedRobotRef = useRef(null);
  const now = useClock();

  const onlineCount = useMemo(() => {
    return robots.filter((r) => r.status && r.status.toLowerCase() !== 'offline').length;
  }, [robots]);

  useEffect(() => {
    selectedRobotRef.current = selectedRobot;
  }, [selectedRobot]);

  const fetchRobots = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/robot-control/api/robots', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setRobots(response.data);

      const initialVelocities = {};
      response.data.forEach((robot) => {
        initialVelocities[robot.id] = DEFAULT_VELOCITY;
      });
      setVelocityInputs(initialVelocities);

      setSelectedRobot((currentRobot) => {
        if (currentRobot || response.data.length === 0) {
          return currentRobot;
        }
        return response.data[0];
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching robots:', err);
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    if (token) {
      fetchRobots();

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new ReconnectingWebSocket(`${wsProtocol}//${window.location.host}/robot-control/api/robots/updates?token=${encodeURIComponent(token)}`);

      ws.onstatechange = (state) => {
        setWsConnected(state === 'connected');
      };

      ws.onmessage = (event) => {
        try {
          const update = JSON.parse(event.data);
          const { robotId, status } = update;

          if (update.type === 'log') {
            if (selectedRobotRef.current?.id === robotId) {
              setLogs((currentLogs) => {
                const nextEntry = update.entry;
                const existingIndex = currentLogs.findIndex((entry) => entry.id === nextEntry.id);
                if (existingIndex >= 0) {
                  const nextLogs = [...currentLogs];
                  nextLogs[existingIndex] = nextEntry;
                  return nextLogs;
                }
                return [nextEntry, ...currentLogs].slice(0, 300);
              });
            }
            return;
          }

          setRobots((prevRobots) =>
            prevRobots.map((robot) =>
              robot.id === robotId ? { ...robot, status } : robot
            )
          );
          setSelectedRobot((currentRobot) =>
            currentRobot?.id === robotId ? { ...currentRobot, status } : currentRobot
          );
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      wsRef.current = ws;

      return () => {
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [token, fetchRobots]);

  const sendCommand = async (robotId, command) => {
    setCommandLoading(prev => ({ ...prev, [`${robotId}-${command}`]: true }));

    try {
      await axios.post(`/robot-control/api/robots/${robotId}/command`,
        { command },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (err) {
      console.error('Error sending command:', err);
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setCommandLoading(prev => ({ ...prev, [`${robotId}-${command}`]: false }));
    }
  };

  const sendVelocity = async (robotId, direction) => {
    const velocity = parseFloat(velocityInputs[robotId]) || parseFloat(DEFAULT_VELOCITY);
    if (velocity < 0 || velocity > MAX_VELOCITY) {
      setError(`${messages.velocity}: 0-${MAX_VELOCITY.toFixed(1)}`);
      return;
    }

    setVelocityLoading(prev => ({ ...prev, [`${robotId}-${direction}`]: true }));

    try {
      await axios.post(`/robot-control/api/robots/${robotId}/velocity`,
        { direction, velocity },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (err) {
      console.error('Error sending velocity:', err);
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setVelocityLoading(prev => ({ ...prev, [`${robotId}-${direction}`]: false }));
    }
  };

  const handleVelocityChange = (robotId, value) => {
    setVelocityInputs(prev => ({ ...prev, [robotId]: value }));
  };

  // Keyboard driving on the drive tab: WASD / arrows to move, space to stop
  useEffect(() => {
    if (activeSubmenu !== 'drive' || !selectedRobot) {
      return undefined;
    }

    const directionByKey = {
      w: 'forward', ц: 'forward', arrowup: 'forward',
      s: 'back', ы: 'back', arrowdown: 'back',
      a: 'left', ф: 'left', arrowleft: 'left',
      d: 'right', в: 'right', arrowright: 'right'
    };

    const robotId = selectedRobot.id;
    const handleKeyDown = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === ' ') {
        event.preventDefault();
        sendVelocity(robotId, 'stop');
        return;
      }
      const direction = directionByKey[key];
      if (direction) {
        event.preventDefault();
        sendVelocity(robotId, direction);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubmenu, selectedRobot, velocityInputs, token]);

  const loadSchedule = async (robotId) => {
    try {
      setScheduleLoading(true);
      setScheduleMessage('');
      const response = await axios.get(`/robot-control/api/robots/${robotId}/schedule`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setScheduleData(response.data);
      setScheduleMessage(messages.scheduleLoaded);
      setError(null);
    } catch (err) {
      console.error('Error loading schedule:', err);
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      setError(err.response?.data || messages.failedLoadSchedule);
    } finally {
      setScheduleLoading(false);
    }
  };

  const saveSchedule = async (robotId) => {
    if (!scheduleData) {
      setError(messages.loadScheduleFirst);
      return;
    }

    try {
      setScheduleSaving(true);
      setScheduleMessage('');
      await axios.put(`/robot-control/api/robots/${robotId}/schedule`, scheduleData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setScheduleMessage(messages.scheduleSent);
      setError(null);
    } catch (err) {
      console.error('Error saving schedule:', err);
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      setError(err.response?.data || messages.failedSaveSchedule);
    } finally {
      setScheduleSaving(false);
    }
  };

  const getScheduleSheet = () => {
    if (!scheduleData) {
      return { sheetName: '', headers: [], rows: [] };
    }

    const [sheetName, sheetRows] = Object.entries(scheduleData)[0] || ['', []];
    const rows = Array.isArray(sheetRows) ? sheetRows : [];
    return {
      sheetName,
      headers: rows[0] || [],
      rows: rows.slice(1)
    };
  };

  const updateScheduleCell = (rowIndex, columnIndex, value) => {
    const { sheetName, headers, rows } = getScheduleSheet();
    if (!sheetName) {
      return;
    }

    const updatedRows = rows.map((row, index) => {
      if (index !== rowIndex) {
        return row;
      }
      const nextRow = [...row];
      nextRow[columnIndex] = value;
      return nextRow;
    });

    setScheduleData({
      ...scheduleData,
      [sheetName]: [headers, ...updatedRows]
    });
  };

  const addScheduleRow = () => {
    const { sheetName, headers, rows } = getScheduleSheet();
    if (!sheetName || headers.length === 0) {
      return;
    }

    setScheduleData({
      ...scheduleData,
      [sheetName]: [headers, ...rows, headers.map(() => '')]
    });
  };

  const deleteScheduleRow = (rowIndex) => {
    const { sheetName, headers, rows } = getScheduleSheet();
    if (!sheetName) {
      return;
    }

    setScheduleData({
      ...scheduleData,
      [sheetName]: [headers, ...rows.filter((_, index) => index !== rowIndex)]
    });
  };

  const loadLogs = async (robotId) => {
    try {
      setLogsLoading(true);
      const response = await axios.get(`/robot-control/api/robots/${robotId}/logs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setLogs(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading logs:', err);
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      setError(err.response?.data || messages.failedLoadLogs);
    } finally {
      setLogsLoading(false);
    }
  };

  const updateLidarMap = async (robotId) => {
    const distance = Math.max(1, Math.min(parseInt(lidarDistance, 10) || 5, 30));
    setLidarDistance(distance);

    try {
      setLidarLoading(true);
      setLidarMessage('');
      const response = await axios.post(`/robot-control/api/robots/${robotId}/lidar-map`,
        { distance },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setLidarMap(response.data);
      setLidarVisible(true);
      setLidarMessage(messages.lidarUpdated);
      setError(null);
    } catch (err) {
      console.error('Error loading lidar map:', err);
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      setError(err.response?.data || messages.failedLoadLidar);
    } finally {
      setLidarLoading(false);
    }
  };

  const handleRobotSelect = (robot) => {
    setSelectedRobot(robot);
    setScheduleData(null);
    setScheduleMessage('');
    setLogs([]);
    setLogFilter('all');
    setLidarMap(null);
    setLidarMessage('');
    setLidarVisible(true);
    setActiveSubmenu('drive');
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  // Load logs whenever the selected robot changes (feeds the logs tab and
  // the sidebar "Messages" card); live updates arrive over the WebSocket.
  const selectedRobotId = selectedRobot?.id;
  useEffect(() => {
    if (selectedRobotId) {
      loadLogs(selectedRobotId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRobotId]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const downloadLogs = () => {
    if (!selectedRobot || logs.length === 0) {
      return;
    }
    const lines = logs.map((entry) => {
      const repeat = entry.repeatCount > 1 ? ` (x${entry.repeatCount})` : '';
      return `${new Date(entry.lastReceivedAt || entry.receivedAt).toLocaleString()}${repeat}\t${entry.message}`;
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedRobot.id}-logs.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const { sheetName: scheduleSheetName, headers: scheduleHeaders, rows: scheduleRows } = getScheduleSheet();

  const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const dateStr = now.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });

  const statusLower = selectedRobot ? selectedRobot.status.toLowerCase() : '';
  const isOnline = statusLower && statusLower !== 'offline';

  const logsWithSeverity = useMemo(() => {
    return logs.map((entry) => ({ ...entry, severity: getLogSeverity(entry.message) }));
  }, [logs]);

  const filteredLogs = useMemo(() => {
    if (logFilter === 'errors') {
      return logsWithSeverity.filter((entry) => entry.severity === 'error');
    }
    if (logFilter === 'important') {
      return logsWithSeverity.filter((entry) => entry.severity !== 'ok');
    }
    return logsWithSeverity;
  }, [logsWithSeverity, logFilter]);

  const logStats = useMemo(() => {
    return {
      total: logsWithSeverity.length,
      errors: logsWithSeverity.filter((entry) => entry.severity === 'error').length,
      warnings: logsWithSeverity.filter((entry) => entry.severity === 'warning').length
    };
  }, [logsWithSeverity]);

  const repeatedLogs = useMemo(() => {
    return logsWithSeverity
      .filter((entry) => entry.repeatCount > 1)
      .slice(0, 3);
  }, [logsWithSeverity]);

  const sidebarAlerts = useMemo(() => {
    const alerts = logsWithSeverity.filter((entry) => entry.severity !== 'ok');
    return (alerts.length > 0 ? alerts : logsWithSeverity).slice(0, 3);
  }, [logsWithSeverity]);

  const scheduleStats = useMemo(() => {
    if (!scheduleData) {
      return null;
    }
    const { headers, rows } = getScheduleSheet();
    if (headers.length === 0) {
      return null;
    }
    const timeColumn = headers.findIndex((header) => /time|время/i.test(String(header)));
    const columnIndex = timeColumn >= 0 ? timeColumn : 0;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const times = rows
      .map((row) => {
        const match = /(\d{1,2}):(\d{2})/.exec(String(row[columnIndex] ?? ''));
        return match ? parseInt(match[1], 10) * 60 + parseInt(match[2], 10) : null;
      })
      .filter((minutes) => minutes !== null)
      .sort((a, b) => a - b);
    if (times.length === 0) {
      return null;
    }
    const next = times.find((minutes) => minutes > nowMinutes) ?? null;
    const completed = times.filter((minutes) => minutes <= nowMinutes).length;
    const formatTime = (minutes) => {
      const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
      const mm = String(minutes % 60).padStart(2, '0');
      return `${hh}:${mm}`;
    };
    let inLabel = '';
    if (next !== null) {
      const diff = next - nowMinutes;
      const hours = Math.floor(diff / 60);
      const mins = diff % 60;
      const parts = [];
      if (hours > 0) {
        parts.push(`${hours} ${messages.hoursShort}`);
      }
      if (mins > 0 || hours === 0) {
        parts.push(`${mins} ${messages.minutesShort}`);
      }
      inLabel = `${messages.timeIn} ${parts.join(' ')}`;
    }
    return {
      total: times.length,
      completed,
      remaining: times.length - completed,
      next,
      nextLabel: next !== null ? formatTime(next) : null,
      inLabel,
      times
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleData, now, messages.hoursShort, messages.minutesShort, messages.timeIn]);

  const renderRobotStatusPill = (status, extraClass = '') => {
    const offline = status.toLowerCase() === 'offline';
    return (
      <span className={`status-pill ${offline ? 'offline' : 'online'} ${extraClass}`}>
        <span className="status-pill-dot" />
        {offline ? messages.statusOffline : messages.statusOnline}
      </span>
    );
  };

  const renderLogIcon = (severity) => {
    if (severity === 'error') {
      return <span className="log-icon log-icon-error"><XCircleIcon /></span>;
    }
    if (severity === 'warning') {
      return <span className="log-icon log-icon-warning"><AlertTriangleIcon /></span>;
    }
    return <span className="log-icon log-icon-ok"><CheckCircleIcon /></span>;
  };

  return (
    <div className="control-panel">
      <header className="panel-header">
        <div className="header-content">
          <button
            onClick={toggleSidebar}
            className="menu-toggle"
            aria-label={messages.toggleMenu}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div className="brand">
            <span className="brand-logo"><WheatIcon /></span>
            <div className="brand-text">
              <h1>{messages.appTitle}</h1>
              <span className="brand-subtitle">{messages.appSubtitle}</span>
            </div>
          </div>
          <div className="header-actions">
            <div className="header-clock" title={dateStr}>
              <strong>{timeStr}</strong>
              <span>{dateStr}</span>
            </div>
            <span
              className={`ws-indicator ${wsConnected ? 'ws-connected' : 'ws-disconnected'}`}
              title={wsConnected ? messages.connectionOk : messages.connectionLost}
              aria-label={wsConnected ? messages.connectionOk : messages.connectionLost}
            >
              <span className="ws-dot" />
            </span>
            <div className="language-switch panel-language-switch" aria-label={messages.language}>
              <button
                type="button"
                className={language === 'ru' ? 'active' : ''}
                onClick={() => onLanguageChange('ru')}
                title={messages.language}
              >
                RU
              </button>
              <button
                type="button"
                className={language === 'en' ? 'active' : ''}
                onClick={() => onLanguageChange('en')}
                title={messages.language}
              >
                EN
              </button>
            </div>
            <button
              onClick={onThemeToggle}
              className="theme-toggle"
              title={theme === 'dark' ? messages.lightTheme : messages.darkTheme}
              aria-label={theme === 'dark' ? messages.lightTheme : messages.darkTheme}
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
            <button onClick={onLogout} className="logout-button">{messages.logout}</button>
          </div>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <div className="panel-content">
        <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <div className="sidebar-card sidebar-robots">
            <div className="sidebar-header">
              <h3>{messages.robots}</h3>
              <span className="sidebar-subtitle">
                {onlineCount} {messages.of} {robots.length} {messages.online}
              </span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="close-sidebar"
                aria-label={messages.closeMenu}
              >
                ×
              </button>
            </div>

            <div className="robots-list">
              {loading && <div className="loading">{messages.loadingRobots}</div>}
              {!loading && robots.length === 0 && (
                <div className="loading">{messages.noRobots}</div>
              )}
              {robots.map(robot => (
                <div
                  key={robot.id}
                  className={`robot-item ${selectedRobot?.id === robot.id ? 'selected' : ''}`}
                  onClick={() => handleRobotSelect(robot)}
                >
                  <span className={`robot-icon ${robot.status.toLowerCase() === 'offline' ? 'offline' : ''}`}>
                    <TractorIcon />
                  </span>
                  <div className="robot-info">
                    <div className="robot-name-row">
                      <h4>{robot.description}</h4>
                      {renderRobotStatusPill(robot.status)}
                    </div>
                    <p>{messages.id} {robot.id}</p>
                    <p>{messages.ip} {robot.ip}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sidebar-card farm-card">
            <div className="farm-header">
              <h3>{messages.farmName}</h3>
              <span className="farm-icon"><BarnIcon /></span>
            </div>
            <div className="farm-status">
              <span className="farm-status-dot" />
              {messages.farmOnline}: {onlineCount} {messages.of} {robots.length}
            </div>
          </div>

          <div className="sidebar-card messages-card">
            <h3>{messages.messagesTitle}</h3>
            {sidebarAlerts.length === 0 ? (
              <p className="messages-empty">{messages.noMessages}</p>
            ) : (
              <div className="messages-list">
                {sidebarAlerts.map((entry) => (
                  <div className={`message-card ${entry.severity}`} key={`alert-${entry.id}`}>
                    <span className="message-card-icon"><AlertTriangleIcon /></span>
                    <div className="message-card-body">
                      <p>{entry.message}</p>
                      <time>{formatLogTime(entry)}</time>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {sidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="main-content">
          {selectedRobot ? (
            <div className="robot-workspace">
              <section className="robot-header-card">
                <div className="robot-header-info">
                  <p className="robot-kicker">{messages.selectedRobot}</p>
                  <div className="robot-title-row">
                    <h2>{selectedRobot.description}</h2>
                    <span className="robot-meta">{messages.id} {selectedRobot.id}</span>
                    <span className="robot-meta">{messages.ip} {selectedRobot.ip}</span>
                  </div>
                </div>
                {renderRobotStatusPill(selectedRobot.status, 'status-pill-lg')}
              </section>

              <nav className="submenu-tabs" role="tablist" aria-label={messages.other}>
                <button
                  type="button"
                  className={`submenu-tab ${activeSubmenu === 'drive' ? 'active' : ''}`}
                  onClick={() => setActiveSubmenu('drive')}
                >
                  {messages.drive}
                </button>
                <button
                  type="button"
                  className={`submenu-tab ${activeSubmenu === 'timetable' ? 'active' : ''}`}
                  onClick={() => setActiveSubmenu('timetable')}
                >
                  {messages.timetable}
                </button>
                <button
                  type="button"
                  className={`submenu-tab ${activeSubmenu === 'logs' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveSubmenu('logs');
                    if (logs.length === 0) {
                      loadLogs(selectedRobot.id);
                    }
                  }}
                >
                  {messages.logs}
                </button>
                <button
                  type="button"
                  className={`submenu-tab ${activeSubmenu === 'other' ? 'active' : ''}`}
                  onClick={() => setActiveSubmenu('other')}
                >
                  {messages.other}
                </button>
              </nav>

              <div className="workspace-columns">
                <div className="workspace-main">
                  {activeSubmenu === 'drive' && (
                    <>
                      <div className="status-strip">
                        <div className="status-strip-item">
                          <span className="status-strip-icon status-strip-icon-success"><BroadcastIcon /></span>
                          <div>
                            <span className="status-strip-label">{messages.status}</span>
                            <strong className={isOnline ? 'status-online-text' : ''}>
                              {isOnline ? messages.statusOnline : messages.statusOffline}
                            </strong>
                          </div>
                        </div>
                        <div className="status-strip-item">
                          <span className="status-strip-icon"><BatteryIcon /></span>
                          <div>
                            <span className="status-strip-label">{messages.battery}</span>
                            <div className="status-strip-value-row">
                              <strong>85%</strong>
                              <span className="battery-bar"><span className="battery-bar-fill" style={{ width: '85%' }} /></span>
                            </div>
                          </div>
                        </div>
                        <div className="status-strip-item">
                          <span className="status-strip-icon"><SignalIcon /></span>
                          <div>
                            <span className="status-strip-label">{messages.signal}</span>
                            <div className="status-strip-value-row">
                              <strong>{messages.strong}</strong>
                              <span className="status-strip-note">-48 {messages.dbm}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="video-shell">
                        <RobotVideo
                          robotId={selectedRobot.id}
                          token={token}
                          messages={messages}
                        />
                      </div>
                    </>
                  )}

                  {activeSubmenu === 'timetable' && (
                    <div className="submenu-panel">
                      <div className="schedule-section">
                        <div className="schedule-header">
                          <h3><span className="panel-title-icon"><CalendarIcon /></span>{messages.startTimetable}</h3>
                          <div className="schedule-actions">
                            <button onClick={() => loadSchedule(selectedRobot.id)} disabled={scheduleLoading || scheduleSaving} className="schedule-button">
                              {scheduleLoading ? messages.loading : messages.load}
                            </button>
                            <button onClick={addScheduleRow} disabled={!scheduleData || scheduleLoading || scheduleSaving} className="schedule-button">
                              {messages.addRow}
                            </button>
                            <button onClick={() => saveSchedule(selectedRobot.id)} disabled={!scheduleData || scheduleLoading || scheduleSaving} className="schedule-button save">
                              {scheduleSaving ? messages.saving : messages.save}
                            </button>
                          </div>
                        </div>

                        {scheduleMessage && (
                          <div className="schedule-message">
                            <span className="schedule-message-dot" />
                            {scheduleMessage}
                            <span className="schedule-message-time">{messages.lastUpdate} {timeStr}</span>
                          </div>
                        )}

                        {!scheduleData && !scheduleLoading && (
                          <div className="schedule-empty">{messages.loadScheduleFromRobot}</div>
                        )}

                        {scheduleData && (
                          <div className="schedule-table-wrap">
                            <div className="schedule-sheet-name">{scheduleSheetName}</div>
                            <table className="schedule-table">
                              <thead>
                                <tr>
                                  {scheduleHeaders.map((header, index) => (
                                    <th key={`${header}-${index}`}>{header}</th>
                                  ))}
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {scheduleRows.map((row, rowIndex) => (
                                  <tr key={`schedule-row-${rowIndex}`}>
                                    {scheduleHeaders.map((_, columnIndex) => (
                                      <td key={`schedule-cell-${rowIndex}-${columnIndex}`}>
                                        <input
                                          value={row[columnIndex] || ''}
                                          onChange={(event) => updateScheduleCell(rowIndex, columnIndex, event.target.value)}
                                          className="schedule-input"
                                        />
                                      </td>
                                    ))}
                                    <td>
                                      <button onClick={() => deleteScheduleRow(rowIndex)} disabled={scheduleSaving} className="schedule-delete" aria-label={messages.deleteScheduleRow} title={messages.delete}>
                                        <TrashIcon />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeSubmenu === 'logs' && (
                    <div className="submenu-panel">
                      <div className="logs-section">
                        <div className="logs-header">
                          <h3>{messages.logs}</h3>
                          <span className="logs-live-indicator">
                            <span className="logs-live-dot" />
                            {logsLoading ? messages.loadingLogs : messages.liveLogs}
                          </span>
                        </div>

                        {!logsLoading && filteredLogs.length === 0 && (
                          <div className="schedule-empty">{messages.noLogs}</div>
                        )}

                        {filteredLogs.length > 0 && (
                          <div className="logs-list">
                            {filteredLogs.map((entry) => (
                              <div className={`log-row log-${entry.severity}`} key={entry.id}>
                                {renderLogIcon(entry.severity)}
                                <time>{formatLogTime(entry)}</time>
                                <p>
                                  {entry.message}
                                </p>
                                {entry.repeatCount > 1 && (
                                  <span className="log-repeat">×{entry.repeatCount}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeSubmenu === 'other' && (
                    <div className="submenu-panel">
                      <div className="other-section">
                        <div className="other-header">
                          <h3><span className="panel-title-icon"><BroadcastIcon /></span>{messages.lidarMap}</h3>
                        </div>

                        {lidarMessage && (
                          <div className="schedule-message">
                            <span className="schedule-message-dot" />
                            {lidarMessage}
                            {lidarMap?.receivedAt && (
                              <span className="schedule-message-time">
                                {messages.lastUpdate} {new Date(lidarMap.receivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="lidar-view">
                          {lidarMap?.image && lidarVisible ? (
                            <>
                              <img src={lidarMap.image} alt={messages.lidarMap} />
                              <div className="lidar-meta">
                                <span>{messages.lidarDistance}: {lidarMap.distance}</span>
                                <span>{messages.lastUpdate}: {new Date(lidarMap.receivedAt).toLocaleString()}</span>
                              </div>
                            </>
                          ) : lidarMap?.image ? (
                            <div className="schedule-empty">{messages.showLidar}</div>
                          ) : (
                            <div className="schedule-empty">{messages.noLidarMap}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <aside className="side-panel">
                  {activeSubmenu === 'drive' && (
                    <div className="side-panel-card">
                      <h3 className="side-panel-title">{messages.controls}</h3>

                      <div className="velocity-block">
                        <div className="velocity-block-header">
                          <span>{messages.velocity}</span>
                          <strong>{velocityInputs[selectedRobot.id] || DEFAULT_VELOCITY} {messages.speedUnit}</strong>
                        </div>
                        <input
                          type="range"
                          step={VELOCITY_STEP}
                          min="0"
                          max={MAX_VELOCITY.toFixed(1)}
                          value={velocityInputs[selectedRobot.id] || DEFAULT_VELOCITY}
                          onChange={(e) => handleVelocityChange(selectedRobot.id, e.target.value)}
                          className="velocity-slider"
                          aria-label={messages.velocity}
                        />
                      </div>

                      <div className="velocity-grid">
                        <button onClick={() => sendVelocity(selectedRobot.id, 'forward-left')} disabled={velocityLoading[`${selectedRobot.id}-forward-left`]} className="velocity-button velocity-up-left" aria-label="forward-left">
                          {velocityLoading[`${selectedRobot.id}-forward-left`] ? '...' : '↖'}
                        </button>
                        <button onClick={() => sendVelocity(selectedRobot.id, 'forward')} disabled={velocityLoading[`${selectedRobot.id}-forward`]} className="velocity-button velocity-up" aria-label="forward">
                          {velocityLoading[`${selectedRobot.id}-forward`] ? '...' : '↑'}
                        </button>
                        <button onClick={() => sendVelocity(selectedRobot.id, 'forward-right')} disabled={velocityLoading[`${selectedRobot.id}-forward-right`]} className="velocity-button velocity-up-right" aria-label="forward-right">
                          {velocityLoading[`${selectedRobot.id}-forward-right`] ? '...' : '↗'}
                        </button>
                        <button onClick={() => sendVelocity(selectedRobot.id, 'left')} disabled={velocityLoading[`${selectedRobot.id}-left`]} className="velocity-button velocity-left" aria-label="left">
                          {velocityLoading[`${selectedRobot.id}-left`] ? '...' : '←'}
                        </button>
                        <button onClick={() => sendVelocity(selectedRobot.id, 'stop')} disabled={velocityLoading[`${selectedRobot.id}-stop`]} className="velocity-button velocity-stop" aria-label="stop">
                          <StopSquareIcon />
                        </button>
                        <button onClick={() => sendVelocity(selectedRobot.id, 'right')} disabled={velocityLoading[`${selectedRobot.id}-right`]} className="velocity-button velocity-right" aria-label="right">
                          {velocityLoading[`${selectedRobot.id}-right`] ? '...' : '→'}
                        </button>
                        <button onClick={() => sendVelocity(selectedRobot.id, 'back-left')} disabled={velocityLoading[`${selectedRobot.id}-back-left`]} className="velocity-button velocity-down-left" aria-label="back-left">
                          {velocityLoading[`${selectedRobot.id}-back-left`] ? '...' : '↙'}
                        </button>
                        <button onClick={() => sendVelocity(selectedRobot.id, 'back')} disabled={velocityLoading[`${selectedRobot.id}-back`]} className="velocity-button velocity-down" aria-label="back">
                          {velocityLoading[`${selectedRobot.id}-back`] ? '...' : '↓'}
                        </button>
                        <button onClick={() => sendVelocity(selectedRobot.id, 'back-right')} disabled={velocityLoading[`${selectedRobot.id}-back-right`]} className="velocity-button velocity-down-right" aria-label="back-right">
                          {velocityLoading[`${selectedRobot.id}-back-right`] ? '...' : '↘'}
                        </button>
                      </div>

                      <div className="drive-command-strip">
                        <button onClick={() => sendCommand(selectedRobot.id, 'start')} disabled={commandLoading[`${selectedRobot.id}-start`]} className="command-button start-btn">
                          {commandLoading[`${selectedRobot.id}-start`] ? messages.starting : messages.start}
                        </button>
                        <button onClick={() => sendCommand(selectedRobot.id, 'stop')} disabled={commandLoading[`${selectedRobot.id}-stop`]} className="command-button stop-btn">
                          {commandLoading[`${selectedRobot.id}-stop`] ? messages.stopping : messages.stop}
                        </button>
                        <button onClick={() => sendCommand(selectedRobot.id, 'reset')} disabled={commandLoading[`${selectedRobot.id}-reset`]} className="command-button reset-btn">
                          {commandLoading[`${selectedRobot.id}-reset`] ? messages.resetting : messages.reset}
                        </button>
                      </div>

                      <div className="drive-hints">
                        <p>{messages.wasdHint}</p>
                        <p>{messages.spaceHint}</p>
                      </div>
                    </div>
                  )}

                  {activeSubmenu === 'timetable' && (
                    <div className="side-panel-card">
                      <h3 className="side-panel-title">
                        {messages.nextLaunch}
                        <span className="side-panel-title-icon"><ClockIcon /></span>
                      </h3>
                      {scheduleStats ? (
                        <>
                          {scheduleStats.next !== null ? (
                            <div className="next-launch">
                              <strong>{scheduleStats.nextLabel}</strong>
                              <span>{scheduleStats.inLabel}</span>
                            </div>
                          ) : (
                            <div className="next-launch next-launch-none">
                              <strong>—</strong>
                            </div>
                          )}
                          <div className="side-panel-stats">
                            <p className="side-panel-caption">{messages.today}</p>
                            <div className="side-panel-stat-row">
                              <span>{messages.launchesCount}</span>
                              <strong>{scheduleStats.total}</strong>
                            </div>
                            <div className="side-panel-stat-row">
                              <span>{messages.completedCount}</span>
                              <strong>{scheduleStats.completed}</strong>
                            </div>
                            <div className="side-panel-stat-row">
                              <span>{messages.remainingCount}</span>
                              <strong>{scheduleStats.remaining}</strong>
                            </div>
                          </div>
                          <div className="daily-launches">
                            <p className="side-panel-caption">{messages.dailyLaunches}</p>
                            <div className="daily-launches-track">
                              {scheduleStats.times.map((minutes, index) => {
                                const position = Math.max(0, Math.min(100, ((minutes - 360) / (1200 - 360)) * 100));
                                const isNext = scheduleStats.next !== null && minutes === scheduleStats.next;
                                return (
                                  <span
                                    key={`dot-${index}`}
                                    className={`daily-launch-dot ${isNext ? 'next' : ''}`}
                                    style={{ left: `${position}%` }}
                                  />
                                );
                              })}
                            </div>
                            <div className="daily-launches-scale">
                              <span>06:00</span>
                              <span>20:00</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="side-panel-note">{messages.noScheduleStats}</p>
                      )}
                      <p className="side-panel-note side-panel-footnote">{messages.scheduleNote}</p>
                    </div>
                  )}

                  {activeSubmenu === 'logs' && (
                    <div className="side-panel-card">
                      <h3 className="side-panel-title">
                        {messages.logSummary}
                        <span className="side-panel-title-icon"><ChartIcon /></span>
                      </h3>
                      <div className="side-panel-stats">
                        <p className="side-panel-caption">{messages.today}</p>
                        <div className="side-panel-stat-row">
                          <span>{messages.totalEntries}</span>
                          <strong>{logStats.total}</strong>
                        </div>
                        <div className="side-panel-stat-row">
                          <span>{messages.errorsCount}</span>
                          <strong className="stat-danger">{logStats.errors}</strong>
                        </div>
                        <div className="side-panel-stat-row">
                          <span>{messages.warningsCount}</span>
                          <strong className="stat-warning">{logStats.warnings}</strong>
                        </div>
                      </div>

                      <div className="log-filter">
                        <p className="side-panel-caption">{messages.filterLabel}</p>
                        <div className="log-filter-buttons">
                          <button type="button" className={logFilter === 'all' ? 'active' : ''} onClick={() => setLogFilter('all')}>
                            {messages.filterAll}
                          </button>
                          <button type="button" className={logFilter === 'errors' ? 'active' : ''} onClick={() => setLogFilter('errors')}>
                            {messages.filterErrors}
                          </button>
                          <button type="button" className={logFilter === 'important' ? 'active' : ''} onClick={() => setLogFilter('important')}>
                            {messages.filterImportant}
                          </button>
                        </div>
                      </div>

                      {repeatedLogs.length > 0 && (
                        <div className="repeated-events">
                          <p className="side-panel-caption">{messages.repeatedEvents}</p>
                          {repeatedLogs.map((entry) => (
                            <div className="repeated-event" key={`repeated-${entry.id}`}>
                              {renderLogIcon(entry.severity)}
                              <span className="repeated-event-text">{entry.message}</span>
                              <span className={`log-repeat ${entry.severity === 'error' ? 'log-repeat-error' : ''}`}>×{entry.repeatCount}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <button type="button" className="download-log-button" onClick={downloadLogs} disabled={logs.length === 0}>
                        <DownloadIcon />
                        {messages.downloadLog}
                      </button>

                      <p className="side-panel-note side-panel-footnote">{messages.logsAutoNote}</p>
                    </div>
                  )}

                  {activeSubmenu === 'other' && (
                    <div className="side-panel-card">
                      <h3 className="side-panel-title">
                        {messages.mapSettings}
                        <span className="side-panel-title-icon"><SlidersIcon /></span>
                      </h3>

                      <div className="lidar-radius-block">
                        <p className="side-panel-caption">{messages.lidarDistance}</p>
                        <div className="lidar-stepper">
                          <button
                            type="button"
                            onClick={() => setLidarDistance((value) => Math.max(1, (parseInt(value, 10) || 5) - 1))}
                            aria-label="-"
                          >
                            −
                          </button>
                          <strong>{lidarDistance}</strong>
                          <button
                            type="button"
                            onClick={() => setLidarDistance((value) => Math.min(30, (parseInt(value, 10) || 5) + 1))}
                            aria-label="+"
                          >
                            +
                          </button>
                        </div>
                        <p className="side-panel-note">{messages.radiusHint}</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => updateLidarMap(selectedRobot.id)}
                        disabled={lidarLoading}
                        className="lidar-update-button"
                      >
                        <RefreshIcon />
                        {lidarLoading ? messages.updatingLidar : messages.updateLidar}
                      </button>
                      {lidarMap?.image && (
                        <button
                          type="button"
                          onClick={() => setLidarVisible((visible) => !visible)}
                          className="lidar-toggle-button"
                        >
                          {lidarVisible ? <EyeOffIcon /> : <EyeIcon />}
                          {lidarVisible ? messages.hideLidar : messages.showLidar}
                        </button>
                      )}

                      <div className="lidar-legend">
                        <p className="side-panel-caption">{messages.legendTitle}</p>
                        <div className="lidar-legend-item"><span className="legend-dot legend-dot-wall" />{messages.legendWalls}</div>
                        <div className="lidar-legend-item"><span className="legend-dot legend-dot-obstacle" />{messages.legendObstacles}</div>
                        <div className="lidar-legend-item"><span className="legend-dot legend-dot-robot" />{messages.legendRobot}</div>
                      </div>

                      <div className="lidar-info-note">
                        <span className="lidar-info-icon">i</span>
                        <p>{messages.lidarInfoNote}</p>
                      </div>

                      <p className="side-panel-note side-panel-footnote">{messages.lidarRedNote}</p>
                    </div>
                  )}
                </aside>
              </div>
            </div>
          ) : (
            <div className="no-robot-selected">
              <div className="empty-content">
                <div className="empty-icon">
                  <RobotIcon />
                </div>
                <h3>{messages.selectRobot}</h3>
                <p>{messages.selectRobotHint}</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ControlPanel;
